<template lang="pug">
DynamicMedia.hero(
  :config="cfg"
  :widths="[768, 1280, 1920]"
  :theme="mediaTheme"
  :preload="768"
  fetchPriority="high"
  ref="heroRef"
)
  ImageFlag.hero-image-flag(
    v-if="product && product.sku !== IMAGE_FLAG_EXCLUDED_SKU && imageFlagHero",
    :product="product"
    :imageFlagHero="imageFlagHero"
  )
  .hero-content(:class="{ 'full-width': theme === 'none' }")
    slot
    .switcher(v-if="sliderControls")
      .switcher-controls
        button.a11y(@click="scroll(-1)" :disabled="atFirstSlide" aria-label="Previous Slide")
          Icon(name="arrow-left" fill="white")
        .text {{ sliderControls.index }}/{{ sliderControls.max }}
        button.a11y(@click="scroll(1)" :disabled="atLastSlide" aria-label="Next Slide")
          Icon(name="arrow-right" fill="white")
</template>
<script setup lang="ts">
import { computed, inject, onMounted, ref } from 'vue'
import { carouselContextKey } from './carousel-context'
import type { MediaConfig } from '../../js/sisuTypes'

// Content-specific exclusion; belongs in CMS config rather than a shared component
const IMAGE_FLAG_EXCLUDED_SKU = 'RM-43'

interface Product {
  sku?: string
  name?: string
  gallery?: Array<{
    link?: string
    label?: string[]
    width?: number
    height?: number
  }>
}

interface SliderControls {
  index: number
  max: number
}

const props = withDefaults(defineProps<{
  background?: MediaConfig
  // Inert here — product-fns.ts reads it upstream and rewrites it into `productImage`.
  // Keep it declared, or it falls through onto the rendered element as an attribute.
  useProductImage?: boolean
  // useFirst, replaceFirst, custom
  productImage?: string
  theme?: string
  bgEffect?: string
  product?: Product
  imageFlagHero?: string
}>(), {
  background      : () => ({ width: 1600, height: 900 }),
  useProductImage : false,
  productImage    : undefined,
  theme           : undefined,
  bgEffect        : undefined,
  product         : () => ({}),
  imageFlagHero   : '',
})

// Only set when this Hero is a slide of a Carousel
const carousel = inject(carouselContextKey, null)

// DynamicMedia is a component, so this holds its public instance rather than a DOM
// node — the rendered element comes off its `$el`.
const heroRef = ref<{ $el?: HTMLElement } | null>(null)
const sliderControls = ref<SliderControls | null>(null)

const mediaTheme = computed(() => {
  const themes: string[] = []

  if (props.bgEffect) {
    themes.push(props.bgEffect)
  }

  if (props.theme) {
    themes.push(props.theme)
  }

  if (!themes.length) {
    return undefined
  }

  return themes.join(' ')
})

const cfg = computed(() => {
  if (!props.productImage || props.productImage === 'custom') {
    return props.background
  }

  const gallery = props.product?.gallery || []
  const img = gallery.find((galleryObj) => (galleryObj.label || []).includes('header_image'))
  let src = (img || gallery[0])?.link

  if (props.productImage === 'replaceFirst' && props.background?.src) {
    src = props.background.src
  }

  return {
    src,
    alt        : props.product?.name,
    height     : props.background?.height || img?.height || 800,
    width      : props.background?.width || img?.width || 2000,
    autoHeight : false,
    preload    : true,
  }
})

function rootEl(): HTMLElement | null {
  return heroRef.value?.$el || null
}

onMounted(() => {
  const el = rootEl()
  const slides = carousel?.contents.value

  // Controls belong to direct slides of a hero-slider, not to Heroes nested deeper inside one
  if (!el || !slides || carousel?.theme.value !== 'hero-slider' || el.parentElement !== slides) {
    return
  }

  const max = slides.children.length

  if (max > 1) {
    sliderControls.value = { index: Array.from(slides.children).indexOf(el) + 1, max }
  }
})

const atFirstSlide = computed(() => (sliderControls.value?.index ?? 1) <= 1)
const atLastSlide = computed(() => (sliderControls.value?.index ?? 1) >= (sliderControls.value?.max ?? 1))

// `offset` is a direction: -1 for the previous slide, 1 for the next one.
// `index` is 1-based, Carousel.scrollTo is 0-based.
function scroll(offset: number) {
  carousel?.scrollTo((sliderControls.value?.index ?? 1) - 1 + offset, true)
}
</script>
<style lang="scss">
// Mobile hero slider styles... for rm43 page
@include mobile() {
  .hero-slider .contents > .media-wrap.hero {
    display: block;
    overflow: visible;
    background-color: $color-white;

    .content {
      margin-left: 0;
    }

    &.horiz-gradient {
      .content {
        background: none
      }
    }

    .hero-content {
      color: black;
      display: flex;
      flex-direction: column;
      width: 100%;
      max-width: 100%;

      > * {
        order: 1;
      }

      .switcher {
        margin-top: 0;
        margin-bottom: $space-s;
        order: 0;
      }

      .icon svg {
        fill: black !important;
      }
    }
  }
}
</style>

<style lang="scss" scoped>

.switcher-controls {
  display: inline-flex;
  align-items: center;
  font-size: $size-s;
  margin-top: $space-s;

  .text {
    margin: 0 $space-m;
  }

  .icon {
    height: 26px;
    width: 26px;
    cursor: pointer;
  }

  button:disabled {
    opacity: 0.35;

    .icon {
      cursor: default;
    }
  }
}

.hero-content {
  color: $color-white;
  max-width: 100%;

  &.full-width {
    width: 100%;
  }

  @include tablet() {
    padding: $space-xxl;
  }
  @include all-desktop() {
    padding: $space-xxl $space-xxxxl;
  }
}

.hero-image-flag {
  position: absolute;
  right: $space-l;
  top: 46px;
}

.scroller .contents .media-wrap.hero .content .hero-image-flag:deep() {
  display: none;
}
</style>
